import * as React from "react";

import { cn } from "@/lib/utils";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, rows, style, ...props }, ref) => {
  const internalRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [isFocused, setIsFocused] = React.useState(false);

  const autoResize = React.useCallback((focused?: boolean) => {
    const textarea = internalRef.current;
    if (!textarea) return;

    const isMobile = window.innerWidth < 768;
    const shouldExpand = (focused !== undefined ? focused : isFocused) && isMobile;

    if (shouldExpand) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit all content + padding
      const newHeight = Math.max(textarea.scrollHeight + 4, 200);
      textarea.style.height = `${newHeight}px`;
      textarea.style.fontSize = '18px';
    } else if (!focused && focused !== undefined) {
      // Reset to default when explicitly unfocused
      textarea.style.height = '';
      textarea.style.fontSize = '';
    }
  }, [isFocused]);

  React.useEffect(() => {
    if (isFocused) {
      autoResize();
    }
  }, [isFocused, autoResize, props.value]);

  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-[16px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-all duration-200",
        className
      )}
      rows={rows}
      ref={(node) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      onFocus={(e) => {
        setIsFocused(true);
        // Call autoResize directly with focused=true to bypass state delay
        setTimeout(() => autoResize(true), 10);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        autoResize(false);
        props.onBlur?.(e);
      }}
      onInput={() => autoResize()}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

export { Textarea };
